/**
 * Choosing which speaker a call comes out of — the earpiece, the loudspeaker,
 * a wired or Bluetooth headset — via the Audio Output Devices API
 * (`HTMLMediaElement.setSinkId`).
 *
 * Support is real but narrow: desktop Chrome/Edge and Chrome on Android have
 * it, Firefox shipped it in 2025, and Safari (desktop and iOS, and therefore
 * every browser on iOS since they all wrap WebKit) has never implemented it.
 * There is no polyfill for routing audio to a specific output device — it is
 * either there or it is not — so every call site feature-detects with
 * `isAudioOutputSelectionSupported` and simply hides the control when it
 * isn't, the same way the call window hides noise suppression's control on
 * browsers without AudioWorklet.
 */

export const isAudioOutputSelectionSupported = () =>
    typeof HTMLMediaElement !== 'undefined' &&
    typeof HTMLMediaElement.prototype.setSinkId === 'function';

/**
 * The output devices this browser can route to, labelled.
 *
 * Labels are blank until some getUserMedia permission has already been
 * granted — a call always has, by the time anyone would open this list — so
 * this is only ever called once a call is active.
 */
export const listAudioOutputs = async () => {
    if (!navigator.mediaDevices?.enumerateDevices) return [];
    const devices = await navigator.mediaDevices.enumerateDevices();
    return devices
        .filter((d) => d.kind === 'audiooutput')
        .map((d) => ({ deviceId: d.deviceId, label: d.label || 'Speaker' }));
};
